// Teks HARGA TAMBAHAN di sisi pembeli (mig 7900, rencana §10 R1) — bagian
// MURNI: label opsi "XXL (+Rp 20.000)" di pemilih varian dan sub-teks
// "Termasuk tambahan ukuran XXL Rp 20.000" di halaman produk.
//
// Tanpa teks ini reseller melihat "-10 %" di samping 120.000 → 110.000 dan
// mengira diskonnya salah hitung (tambahan ditempel SETELAH diskon). Kalimat
// "tidak kena diskon" SENGAJA tidak dipakai di teks pembeli (putusan pemilik
// 2026-10-20) — cukup "termasuk tambahan".
//
// Label opsi hanya dipasang bila angkanya PASTI: tambahan adalah milik
// KOMBINASI, bukan opsi, jadi "+Rp 20.000" pada "XXL" hanya boleh muncul bila
// setiap kombinasi berharga yang memuat XXL (sesuai pilihan grup lain yang
// sedang aktif) membawa tambahan yang sama. Selain itu label dilewati — angka
// yang ditebak di pemilih varian adalah kelas bug #2095 dalam bentuk teks.

import { formatPrice, variantPriceExtra } from "@/lib/pricing";
import type { ProductVariation, VariantGroup } from "@/lib/products";

/**
 * Tambahan yang PASTI ditagih bila opsi `optionId` dipilih bersama pilihan
 * grup lain yang sedang aktif; `null` = tak ada / tak pasti.
 *
 * Kandidat = varian BERHARGA yang memuat opsi itu dan semua pilihan grup lain.
 * Tak ada kandidat (pilihan grup lain tak cocok) → dinilai atas semua varian
 * berharga yang memuat opsi itu. Mode malas (`variations` kosong) → null.
 */
export function optionPriceExtra(
  variations: readonly ProductVariation[],
  optionId: string,
  otherSelectedOptionIds: readonly string[]
): number | null {
  const withOption = variations.filter(
    (v) => v.price_normal !== undefined && (v.optionIds ?? []).includes(optionId)
  );
  if (withOption.length === 0) return null;
  const matching = withOption.filter((v) =>
    otherSelectedOptionIds.every((id) => (v.optionIds ?? []).includes(id))
  );
  const pool = matching.length > 0 ? matching : withOption;
  const extras = new Set(pool.map((v) => variantPriceExtra(v)));
  if (extras.size !== 1) return null;
  const [only] = [...extras];
  return only > 0 ? only : null;
}

/** " (+Rp 20.000)" untuk label opsi; "" bila tanpa tambahan. */
export function priceExtraOptionSuffix(extra: number | null | undefined): string {
  return extra != null && extra > 0 ? ` (+${formatPrice(extra)})` : "";
}

const SIZE_GROUP = /^(ukuran|size|uk\.?)$/i;

/**
 * "ukuran XXL" — subjek kalimat sub-teks, bila produk punya grup bernama
 * Ukuran/Size dan opsinya terpilih. Selain itu null (kalimatnya tanpa subjek).
 *
 * `variations` (bughunt D14-20): ukuran disebut HANYA bila tambahannya memang
 * ditentukan ukuran — setiap varian berharga ber-ukuran sama membawa tambahan
 * yang sama, dan tambahannya berbeda antar-ukuran. Tambahan dari warna (Gold
 * +15.000 di semua ukuran) dulu terbaca "Termasuk tambahan ukuran M". Mode
 * malas / bukti tak cukup → tanpa subjek. Absen = perilaku lama.
 */
export function priceExtraSubject(
  groups: readonly VariantGroup[],
  selectedOptionIds: readonly string[],
  variations?: readonly ProductVariation[]
): string | null {
  for (const g of groups) {
    if (!SIZE_GROUP.test(g.name.trim())) continue;
    const opt = g.options.find((o) => selectedOptionIds.includes(o.id));
    if (!opt || !opt.name.trim()) continue;
    if (variations && !extraVariesByGroup(variations, g)) return null;
    return `ukuran ${opt.name.trim()}`;
  }
  return null;
}

/** Tambahan varian berharga = fungsi opsi grup ini, dan berbeda antar-opsinya. */
function extraVariesByGroup(variations: readonly ProductVariation[], group: VariantGroup): boolean {
  const optionIds = new Set(group.options.map((o) => o.id));
  const byOption = new Map<string, number>();
  for (const v of variations) {
    if (v.price_normal === undefined) continue;
    const opt = (v.optionIds ?? []).find((id) => optionIds.has(id));
    if (!opt) continue;
    const extra = variantPriceExtra(v);
    const seen = byOption.get(opt);
    if (seen === undefined) byOption.set(opt, extra);
    else if (seen !== extra) return false;
  }
  return new Set(byOption.values()).size > 1;
}

/** "Termasuk tambahan ukuran XXL Rp 20.000"; null bila tanpa tambahan. */
export function priceExtraNote(
  extra: number | null | undefined,
  subject?: string | null
): string | null {
  if (extra == null || !(extra > 0)) return null;
  const s = subject?.trim();
  return `Termasuk tambahan ${s ? `${s} ` : ""}${formatPrice(extra)}`;
}
